import { Link, useNavigate } from 'react-router-dom'
import { MapPin, Clock, Star, Truck, Shield, Zap, ShoppingBag, Package, TrendingUp, User, Store as StoreIcon, Settings, Users, BarChart3, Bell } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'
import { Store, Order } from '../types'
import { api } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import { useStore } from '../contexts/StoreContext'
import StoreImage from '../components/StoreImage'
// Logo con texto para la app
const ColmaFacilLogo = '/image/ColmaFacil-Logo.png'
import { calculateStoreIsOpen } from '../lib/storeUtils'
import { useCart } from '../contexts/CartContext'
import NewsCarousel from '../components/NewsCarousel'
import InstallPWA from '../components/InstallPWA'
import SEO from '../components/SEO'
import { getCache, getCacheStale, setCache } from '../lib/cache'

const Home = () => {
  const { user, isAuthenticated, isStoreOwner } = useAuth()
  const { activeStore } = useStore()
  const navigate = useNavigate()
  const { itemCount } = useCart()
  const [featuredStores, setFeaturedStores] = useState<Store[]>([])
  const [loading, setLoading] = useState(true)
  const [userStats, setUserStats] = useState({
    recentOrders: 0,
    favoriteStores: 0
  })
  const [storeOwnerStats, setStoreOwnerStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    totalRevenue: 0,
    activeEmployees: 0
  })
  const [stats, setStats] = useState({
    stores: 0,
    orders: 0,
    users: 0
  })

  // Ref para prevenir ejecuciones duplicadas (React StrictMode)
  const loadingDataRef = useRef(false)

  // No redirigir automáticamente, mostrar contenido personalizado según el usuario

  useEffect(() => {
    // Prevenir ejecuciones duplicadas causadas por React StrictMode
    if (loadingDataRef.current) return
    loadingDataRef.current = true

    const loadData = async () => {
      const cacheKeyStores = 'home_featured_stores'
      const cacheKeyStats = 'home_stats'
      
      // Intentar cargar desde cache primero
      const cachedStoresStale = getCacheStale<Store[]>(cacheKeyStores)
      const cachedStores = getCache<Store[]>(cacheKeyStores) // Solo datos válidos (no expirados)
      const cachedStats = getCache<{ stores: number; orders: number; users: number }>(cacheKeyStats)
      
      // Si hay cache válido, mostrar y no hacer petición
      if (cachedStores && cachedStores.length > 0) {
        setFeaturedStores(cachedStores)
      } else if (cachedStoresStale && cachedStoresStale.length > 0) {
        // Si hay cache expirado, mostrar mientras se actualiza
        setFeaturedStores(cachedStoresStale)
      }
      if (cachedStats) {
        setStats(cachedStats)
      }
      
      try {
        // Cargar stores destacados y estadísticas en paralelo
        const [storesResponse, statsResponse] = await Promise.all([
          api.getStores({ limit: 20, featured: true }),
          api.getPublicStats()
        ])
        
        if (storesResponse.success) {
          // Filtrar solo negocios abiertos para productos destacados
          const allStores = storesResponse.data || []
          const openStores = allStores
            .filter((store: any) => {
              const isActive = store.is_active !== false
              return calculateStoreIsOpen(store.operating_hours, isActive)
            })
            .map((store: any) => {
              // Mapear campos del backend al formato del frontend
              return {
                id: store.id,
                name: store.name,
                slug: store.slug,
                description: store.description || '',
                address: store.address,
                phone: store.phone,
                email: store.email,
                logoUrl: store.logo_url,
                coverImageUrl: store.cover_image_url,
                latitude: store.latitude,
                longitude: store.longitude,
                status: store.status,
                isActive: calculateStoreIsOpen(store.operating_hours, store.is_active !== false && store.is_active !== null),
                offersKitchenService: store.offers_kitchen_service || false,
                offersDelivery: store.offers_delivery !== false,
                deliveryFee: Number(store.delivery_fee) || 0,
                minOrderAmount: Number(store.min_order_amount) || 0,
                deliveryTime: store.delivery_time ? Number(store.delivery_time) : undefined,
                operatingHours: store.operating_hours,
                rating: store.rating ? Number(store.rating) : null,
                reviewCount: store.review_count ? Number(store.review_count) : 0,
                categories: store.categories || []
              }
            })
          setFeaturedStores(openStores)
          
          // Guardar en cache (2 minutos de TTL para actualizaciones más frecuentes)
          setCache(cacheKeyStores, openStores, 2 * 60 * 1000)
        }
        
        if (statsResponse.success && statsResponse.data) {
          const statsData = {
            stores: statsResponse.data.stores || 0,
            orders: statsResponse.data.orders || 0,
            users: statsResponse.data.users || 0
          }
          setStats(statsData)
          
          // Guardar en cache (1 hora de TTL)
          setCache(cacheKeyStats, statsData, 60 * 60 * 1000)
        }

        // Cargar estadísticas personalizadas según el tipo de usuario
        if (isAuthenticated && user) {
          if (user.type === 'customer') {
            try {
              // OPTIMIZACIÓN: Verificar cache de OrderHistory antes de hacer query
              const ordersCacheKey = `orders_history_${user.id}`
              const cachedOrders = getCacheStale<Order[]>(ordersCacheKey)
              
              if (cachedOrders && cachedOrders.length > 0) {
                // Usar cache si está disponible (evita query innecesaria)
                setUserStats({
                  recentOrders: Math.min(cachedOrders.length, 5), // Usar hasta 5 del cache
                  favoriteStores: 0 // TODO: Implementar favoritos
                })
              } else {
                // Solo hacer query si no hay cache
                const ordersResponse = await api.get('/orders?limit=5')
                if (ordersResponse.data.success) {
                  setUserStats({
                    recentOrders: ordersResponse.data.data?.length || 0,
                    favoriteStores: 0 // TODO: Implementar favoritos
                  })
                }
              }
            } catch (error) {
              console.error('Error loading user stats:', error)
            }
          } else if (isStoreOwner && user.storeId) {
            try {
              // OPTIMIZACIÓN: Verificar cache de StoreOwnerDashboard antes de hacer queries
              const dashboardCacheKey = `store_owner_dashboard_${user.storeId}`
              const cachedDashboard = getCacheStale<any>(dashboardCacheKey)
              
              if (cachedDashboard && cachedDashboard.recentOrders) {
                // Usar cache si está disponible (evita queries innecesarias)
                const orders = cachedDashboard.recentOrders || []
                const allOrders = cachedDashboard.totalOrders ? Array(cachedDashboard.totalOrders).fill(null).map((_, i) => orders[i] || null).filter(Boolean) : orders
                const pending = cachedDashboard.pendingOrders || 0
                const revenue = cachedDashboard.todayRevenue || 0
                
                setStoreOwnerStats({
                  totalOrders: cachedDashboard.totalOrders || orders.length,
                  pendingOrders: pending,
                  totalRevenue: revenue,
                  activeEmployees: cachedDashboard.totalEmployees || 0
                })
              } else {
                // Solo hacer queries si no hay cache
                const [ordersResponse, employeesResponse] = await Promise.all([
                  api.get('/orders/store'),
                  api.get(`/auth/store/${user.storeId}/employees`)
                ])
                
                if (ordersResponse.data.success) {
                  const orders = ordersResponse.data.data || []
                  const pending = orders.filter((o: any) => o.status === 'pending' || o.status === 'preparing').length
                  const revenue = orders
                    .filter((o: any) => o.status === 'delivered')
                    .reduce((sum: number, o: any) => sum + (Number(o.total_amount) || 0), 0)
                  
                  setStoreOwnerStats({
                    totalOrders: orders.length,
                    pendingOrders: pending,
                    totalRevenue: revenue,
                    activeEmployees: employeesResponse.data.success ? (employeesResponse.data.data || []).filter((e: any) => e.is_active).length : 0
                  })
                }
              }
            } catch (error) {
              console.error('Error loading store owner stats:', error)
            }
          }
        }
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        setLoading(false)
        loadingDataRef.current = false
      }
    }

    loadData()
  }, [isAuthenticated, user, isStoreOwner])

  return (
    <>
      <SEO
        title="ColmaFácil - Marketplace de Colmados en República Dominicana | Delivery Rápido"
        description="ColmaFácil es el mejor marketplace de colmados en República Dominicana. Compra productos frescos, alimentos, bebidas y más con delivery rápido. Conecta con los mejores colmados de RD y disfruta de precios justos y servicio de calidad."
        keywords="colmados república dominicana, delivery colmados, marketplace colmados, comprar productos frescos RD, delivery rápido dominicana, colmados online, supermercado online RD, comprar alimentos online, delivery comida RD, tienda online dominicana"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "ColmaFácil - Marketplace de Colmados",
          "description": "El mejor marketplace de colmados en República Dominicana",
          "url": "https://colmafacil.store",
          "inLanguage": "es-DO",
          "isPartOf": {
            "@type": "WebSite",
            "name": "ColmaFácil",
            "url": "https://colmafacil.store"
          }
        }}
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Panel Personalizado para Usuarios Logueados - Estilo iMac */}
      {isAuthenticated && user && (
        <section className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white relative overflow-hidden">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '30px 30px'
            }}></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10 lg:py-12">
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <div className="flex-1 min-w-0 pr-4">
                <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 tracking-tight">
                  ¡Bienvenido de vuelta, {user.name?.split(' ')[0] || 'Usuario'}! 👋
                </h2>
                <p className="text-primary-100/90 text-xs sm:text-base font-medium">
                  {user.type === 'customer' 
                    ? 'Continúa explorando y haciendo tus compras'
                    : user.type === 'store_owner'
                    ? 'Gestiona tu negocio desde aquí'
                    : 'Gestiona las operaciones del negocio'}
                </p>
              </div>
              {itemCount > 0 && (
                <Link
                  to="/cart"
                  className="relative p-2.5 sm:p-3.5 bg-white/15 backdrop-blur-md rounded-xl sm:rounded-2xl hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 flex-shrink-0"
                >
                  <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6" />
                  {itemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center shadow-lg border-2 border-white">
                      {itemCount}
                    </span>
                  )}
                </Link>
              )}
            </div>

            {/* Quick Stats Cards - iMac Style - iPhone Optimized */}
            {user.type === 'customer' ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-2.5 md:gap-3">
                <Link
                  to="/orders"
                  className="bg-white/15 backdrop-blur-md rounded-lg sm:rounded-xl md:rounded-2xl p-2.5 sm:p-3 md:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 shadow-lg hover:shadow-xl min-h-[80px] sm:min-h-[90px] flex flex-col items-center justify-center"
                >
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mb-1 sm:mb-2 text-white/90" />
                  <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold">{userStats.recentOrders}</div>
                  <div className="text-[9px] sm:text-[10px] md:text-xs text-primary-100/80 mt-0.5 sm:mt-1 font-medium text-center leading-tight">Pedidos Recientes</div>
                </Link>
                <Link
                  to="/stores"
                  className="bg-white/15 backdrop-blur-md rounded-lg sm:rounded-xl md:rounded-2xl p-2.5 sm:p-3 md:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 shadow-lg hover:shadow-xl min-h-[80px] sm:min-h-[90px] flex flex-col items-center justify-center"
                >
                  <StoreIcon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mb-1 sm:mb-2 text-white/90" />
                  <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold">{featuredStores.length}</div>
                  <div className="text-[9px] sm:text-[10px] md:text-xs text-primary-100/80 mt-0.5 sm:mt-1 font-medium text-center leading-tight">Negocios Disponibles</div>
                </Link>
                <Link
                  to="/cart"
                  className="bg-white/15 backdrop-blur-md rounded-lg sm:rounded-xl md:rounded-2xl p-2.5 sm:p-3 md:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 shadow-lg hover:shadow-xl min-h-[80px] sm:min-h-[90px] flex flex-col items-center justify-center"
                >
                  <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mb-1 sm:mb-2 text-white/90" />
                  <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold">{itemCount}</div>
                  <div className="text-[9px] sm:text-[10px] md:text-xs text-primary-100/80 mt-0.5 sm:mt-1 font-medium text-center leading-tight">En el Carrito</div>
                </Link>
                <Link
                  to="/profile"
                  className="bg-white/15 backdrop-blur-md rounded-lg sm:rounded-xl md:rounded-2xl p-2.5 sm:p-3 md:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 shadow-lg hover:shadow-xl min-h-[80px] sm:min-h-[90px] flex flex-col items-center justify-center"
                >
                  <User className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mb-1 sm:mb-2 text-white/90" />
                  <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold">—</div>
                  <div className="text-[9px] sm:text-[10px] md:text-xs text-primary-100/80 mt-0.5 sm:mt-1 font-medium text-center leading-tight">Mi Perfil</div>
                </Link>
              </div>
            ) : isStoreOwner ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
                <Link
                  to="/store-owner/orders"
                  className="bg-white/15 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 shadow-lg hover:shadow-xl"
                >
                  <Package className="w-5 h-5 sm:w-6 sm:h-6 mb-2 text-white/90" />
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold">{storeOwnerStats.totalOrders}</div>
                  <div className="text-[10px] sm:text-xs text-primary-100/80 mt-1 font-medium">Total Pedidos</div>
                </Link>
                <Link
                  to="/store-owner/orders"
                  className="bg-white/15 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 shadow-lg hover:shadow-xl"
                >
                  <Bell className="w-5 h-5 sm:w-6 sm:h-6 mb-2 text-white/90" />
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold">{storeOwnerStats.pendingOrders}</div>
                  <div className="text-[10px] sm:text-xs text-primary-100/80 mt-1 font-medium">Pendientes</div>
                </Link>
                <Link
                  to="/store-owner/dashboard"
                  className="bg-white/15 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 shadow-lg hover:shadow-xl"
                >
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 mb-2 text-white/90" />
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold">RD${(storeOwnerStats.totalRevenue / 1000).toFixed(1)}k</div>
                  <div className="text-[10px] sm:text-xs text-primary-100/80 mt-1 font-medium">Ingresos</div>
                </Link>
                <div
                  onClick={() => {
                    if (activeStore) {
                      navigate(`/store/${activeStore.id}/employees`)
                    } else {
                      navigate('/store-owner/my-stores')
                    }
                  }}
                  className="bg-white/15 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 cursor-pointer shadow-lg hover:shadow-xl"
                >
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 mb-2 text-white/90" />
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold">{storeOwnerStats.activeEmployees}</div>
                  <div className="text-[10px] sm:text-xs text-primary-100/80 mt-1 font-medium">Empleados</div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3">
                {user.type === 'dispatcher' && (
                  <Link
                    to="/dispatcher"
                    className="bg-white/15 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 shadow-lg hover:shadow-xl"
                  >
                    <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 mb-2 text-white/90" />
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold">—</div>
                    <div className="text-[10px] sm:text-xs text-primary-100/80 mt-1 font-medium">Despacho</div>
                  </Link>
                )}
                {user.type === 'chef' && (
                  <Link
                    to={activeStore ? `/store/${activeStore.id}/kitchen` : '/store/dashboard'}
                    className="bg-white/15 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 shadow-lg hover:shadow-xl"
                  >
                    <Package className="w-5 h-5 sm:w-6 sm:h-6 mb-2 text-white/90" />
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold">—</div>
                    <div className="text-[10px] sm:text-xs text-primary-100/80 mt-1 font-medium">Cocina</div>
                  </Link>
                )}
                {user.type === 'delivery' && (
                  <>
                    <Link
                      to={activeStore ? `/store/${activeStore.id}/delivery` : '/store/delivery'}
                      className="bg-white/15 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 shadow-lg hover:shadow-xl"
                    >
                      <Truck className="w-5 h-5 sm:w-6 sm:h-6 mb-2 text-white/90" />
                      <div className="text-xl sm:text-2xl lg:text-3xl font-bold">—</div>
                      <div className="text-[10px] sm:text-xs text-primary-100/80 mt-1 font-medium">Entregas</div>
                    </Link>
                    <Link
                      to="/profile"
                      className="bg-white/15 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 shadow-lg hover:shadow-xl"
                    >
                      <User className="w-5 h-5 sm:w-6 sm:h-6 mb-2 text-white/90" />
                      <div className="text-xl sm:text-2xl lg:text-3xl font-bold">—</div>
                      <div className="text-[10px] sm:text-xs text-primary-100/80 mt-1 font-medium">Perfil</div>
                    </Link>
                  </>
                )}
                {user.type !== 'delivery' && (
                  <>
                    <Link
                      to="/store/orders"
                      className="bg-white/15 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 shadow-lg hover:shadow-xl"
                    >
                      <Package className="w-5 h-5 sm:w-6 sm:h-6 mb-2 text-white/90" />
                      <div className="text-xl sm:text-2xl lg:text-3xl font-bold">—</div>
                      <div className="text-[10px] sm:text-xs text-primary-100/80 mt-1 font-medium">Pedidos</div>
                    </Link>
                    <Link
                      to="/store/settings"
                      className="bg-white/15 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-white/25 transition-all duration-200 border-2 border-white/20 active:scale-95 shadow-lg hover:shadow-xl"
                    >
                      <Settings className="w-5 h-5 sm:w-6 sm:h-6 mb-2 text-white/90" />
                      <div className="text-xl sm:text-2xl lg:text-3xl font-bold">—</div>
                      <div className="text-[10px] sm:text-xs text-primary-100/80 mt-1 font-medium">Configuración</div>
                    </Link>
                  </>
                )}
              </div>
            )}

            {/* Quick Actions - iMac Style */}
            <div className="mt-4 sm:mt-6 flex flex-wrap gap-2 sm:gap-2.5">
              {user.type === 'customer' && (
                <>
                  <Link
                    to="/stores"
                    className="px-4 sm:px-5 py-2 sm:py-2.5 bg-white/95 backdrop-blur-md text-primary-600 rounded-xl sm:rounded-2xl font-bold hover:bg-white transition-all duration-200 text-xs sm:text-sm shadow-lg hover:shadow-xl active:scale-95"
                  >
                    Explorar Negocios
                  </Link>
                  <Link
                    to="/orders"
                    className="px-4 sm:px-5 py-2 sm:py-2.5 bg-white/15 backdrop-blur-md border-2 border-white/30 text-white rounded-xl sm:rounded-2xl font-bold hover:bg-white/25 transition-all duration-200 text-xs sm:text-sm active:scale-95 shadow-lg hover:shadow-xl"
                  >
                    Ver Mis Pedidos
                  </Link>
                </>
              )}
              {user.type === 'delivery' && (
                <Link
                  to={activeStore ? `/store/${activeStore.id}/delivery` : '/store/delivery'}
                  className="px-4 sm:px-5 py-2 sm:py-2.5 bg-white/95 backdrop-blur-md text-primary-600 rounded-xl sm:rounded-2xl font-bold hover:bg-white transition-all duration-200 text-xs sm:text-sm shadow-lg hover:shadow-xl active:scale-95"
                >
                  Ver Mis Entregas
                </Link>
              )}
              {isStoreOwner && (
                <>
                  <Link
                    to="/store-owner/my-stores"
                    className="px-4 sm:px-5 py-2 sm:py-2.5 bg-white/95 backdrop-blur-md text-primary-600 rounded-xl sm:rounded-2xl font-bold hover:bg-white transition-all duration-200 text-xs sm:text-sm shadow-lg hover:shadow-xl active:scale-95"
                  >
                    Mis Negocios
                  </Link>
                  <Link
                    to="/store-owner/orders"
                    className="px-4 sm:px-5 py-2 sm:py-2.5 bg-white/15 backdrop-blur-md border-2 border-white/30 text-white rounded-xl sm:rounded-2xl font-bold hover:bg-white/25 transition-all duration-200 text-xs sm:text-sm active:scale-95 shadow-lg hover:shadow-xl"
                  >
                    Ver Pedidos
                  </Link>
                  <div
                    onClick={() => {
                      if (activeStore) {
                        navigate(`/store/${activeStore.id}/employees`)
                      } else {
                        navigate('/store-owner/my-stores')
                      }
                    }}
                    className="px-4 sm:px-5 py-2 sm:py-2.5 bg-white/15 backdrop-blur-md border-2 border-white/30 text-white rounded-xl sm:rounded-2xl font-bold hover:bg-white/25 transition-all duration-200 text-xs sm:text-sm active:scale-95 cursor-pointer shadow-lg hover:shadow-xl"
                  >
                    Gestionar Empleados
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Install PWA Banner - Solo para usuarios logueados */}
      {isAuthenticated && (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
          <InstallPWA onHome={true} />
        </div>
      )}

      {/* Hero Section - Solo para usuarios no logueados */}
      {!isAuthenticated && (
        <section className="relative bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 text-white overflow-hidden">
          {/* Background Pattern - iOS Style */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-12 sm:py-16 md:py-20 lg:py-24 xl:py-32 text-center">
            <div className="mb-4 sm:mb-6 md:mb-8 animate-fade-in">
              <div className="inline-block bg-white/95 rounded-xl sm:rounded-2xl px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-6 mb-4 sm:mb-6 md:mb-8 shadow-2xl">
                <img
                  src={ColmaFacilLogo}
                  alt="ColmaFácil"
                  className="mx-auto h-16 sm:h-20 md:h-24 w-auto drop-shadow-2xl"
                />
              </div>
            </div>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 md:mb-6 leading-tight px-2 animate-fade-in-up">
              Tu Negocio en la Palma de tu Mano
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl mb-6 sm:mb-8 md:mb-10 text-primary-100 max-w-3xl mx-auto leading-relaxed px-3 sm:px-4 animate-fade-in-up animation-delay-200">
              Conecta con los mejores negocios de República Dominicana.
              Productos frescos, entrega rápida y precios justos.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-3 sm:px-4 animate-fade-in-up animation-delay-400">
              <Link 
                to="/stores" 
                className="inline-flex items-center justify-center px-5 sm:px-6 md:px-8 py-3 sm:py-3.5 md:py-4 bg-white text-primary-600 rounded-xl sm:rounded-2xl font-semibold text-sm sm:text-base md:text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-200 active:scale-95 min-h-[44px]"
              >
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Explorar Negocios
              </Link>
              <Link 
                to="/register" 
                className="inline-flex items-center justify-center px-5 sm:px-6 md:px-8 py-3 sm:py-3.5 md:py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white rounded-xl sm:rounded-2xl font-semibold text-sm sm:text-base md:text-lg hover:bg-white/20 transition-all duration-200 active:scale-95 min-h-[44px]"
              >
                ¿Quieres ser parte?
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* News Carousel - iPhone Optimized */}
      <section className="py-4 sm:py-6 md:py-8 lg:py-12 bg-gradient-to-b from-white to-gray-50/50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <NewsCarousel />
        </div>
      </section>

      {/* Features Section - Solo para usuarios no logueados */}
      {!isAuthenticated && (
        <>
          <section className="py-8 sm:py-12 md:py-16 lg:py-20 xl:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
              <div className="text-center mb-8 sm:mb-10 md:mb-12 lg:mb-16">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4 px-2">
                  ¿Por qué elegir ColmaFácil?
                </h2>
                <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto px-3">
                  Una experiencia de compra única diseñada para ti
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5 md:gap-6 lg:gap-8">
                <div className="bg-white/80 backdrop-blur-xl rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 shadow-sm border border-gray-100/50 hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-5 md:mb-6 shadow-sm">
                    <MapPin className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-gray-900 text-center">Negocios Cercanos</h3>
                  <p className="text-sm sm:text-base text-gray-600 text-center leading-relaxed">
                    Encuentra los mejores negocios en tu zona con entrega directa a tu puerta
                  </p>
                </div>

                <div className="bg-white/80 backdrop-blur-xl rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 shadow-sm border border-gray-100/50 hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-5 md:mb-6 shadow-sm">
                    <Zap className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-gray-900 text-center">Entrega Rápida</h3>
                  <p className="text-sm sm:text-base text-gray-600 text-center leading-relaxed">
                    Recibe tus productos frescos en menos de 45 minutos
                  </p>
                </div>

                <div className="bg-white/80 backdrop-blur-xl rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 shadow-sm border border-gray-100/50 hover:shadow-md transition-all duration-300 hover:-translate-y-1 sm:col-span-2 md:col-span-1">
                  <div className="bg-gradient-to-br from-green-500 to-green-600 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-5 md:mb-6 shadow-sm">
                    <Shield className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-gray-900 text-center">Productos Frescos</h3>
                  <p className="text-sm sm:text-base text-gray-600 text-center leading-relaxed">
                    Calidad garantizada con los mejores proveedores locales
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats Section - Solo para usuarios no logueados - iPhone Optimized */}
          <section className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-gray-50/50 to-white">
            <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
                <div className="text-center bg-white/80 backdrop-blur-xl rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 shadow-sm border border-gray-100/50">
                  <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary-600 mb-2 sm:mb-3">
                    {loading ? '...' : stats.stores > 0 ? `${stats.stores}+` : '0'}
                  </div>
                  <div className="text-sm sm:text-base md:text-lg text-gray-600 font-medium">Negocios Activos</div>
                </div>
                <div className="text-center bg-white/80 backdrop-blur-xl rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 shadow-sm border border-gray-100/50">
                  <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary-600 mb-2 sm:mb-3">
                    {loading ? '...' : stats.orders > 0 ? `${stats.orders}+` : '0'}
                  </div>
                  <div className="text-sm sm:text-base md:text-lg text-gray-600 font-medium">Pedidos del Mes</div>
                </div>
                <div className="text-center bg-white/80 backdrop-blur-xl rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 shadow-sm border border-gray-100/50">
                  <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary-600 mb-2 sm:mb-3">
                    {loading ? '...' : stats.users > 0 ? `${stats.users}+` : '0'}
                  </div>
                  <div className="text-sm sm:text-base md:text-lg text-gray-600 font-medium">Clientes Satisfechos</div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

 

      {/* Featured Stores */}
      <section className={`py-16 sm:py-20 lg:py-24 ${isAuthenticated ? 'bg-gradient-to-b from-gray-50/50 to-white' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {isAuthenticated ? 'Negocios Disponibles' : 'Negocios Destacados'}
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              {isAuthenticated ? 'Explora los negocios cerca de ti' : 'Descubre los mejores negocios de la comunidad'}
            </p>
          </div>

          {loading ? (
            <div className="flex gap-3 sm:gap-4 md:gap-6 overflow-x-auto pb-4 scrollbar-hide">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex-shrink-0 w-[280px] sm:w-80 bg-gray-100 rounded-2xl sm:rounded-3xl overflow-hidden animate-pulse">
                  <div className="h-40 sm:h-48 bg-gray-200"></div>
                  <div className="p-4 sm:p-6">
                    <div className="h-5 sm:h-6 bg-gray-200 rounded mb-2 sm:mb-3"></div>
                    <div className="h-3 sm:h-4 bg-gray-200 rounded mb-1.5 sm:mb-2"></div>
                    <div className="h-3 sm:h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : featuredStores.length > 0 ? (
            <div className="relative">
              <div className="flex gap-3 sm:gap-4 md:gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory scroll-smooth">
                {featuredStores.map((store) => (
                  <Link
                    key={store.id}
                    to={`/stores/${store.id}`}
                    className="flex-shrink-0 w-[280px] sm:w-80 md:w-96 group bg-white/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100/50 hover:shadow-md transition-all duration-300 overflow-hidden hover:-translate-y-1 active:scale-[0.98] snap-start"
                  >
                  <div className="relative h-44 sm:h-48 md:h-56 overflow-hidden">
                    <StoreImage
                      imageKey={store.coverImageUrl || null}
                      type="cover"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      fallback="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=400&fit=crop"
                      alt={store.name}
                      storeId={store.id}
                    />
                    {store.rating != null && typeof store.rating === 'number' && store.rating > 0 && (
                      <div className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-white/95 backdrop-blur-md px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full shadow-sm border border-white/50">
                        <div className="flex items-center space-x-0.5 sm:space-x-1">
                          <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-yellow-400 fill-current" />
                          <span className="text-[10px] sm:text-xs font-semibold text-gray-900">
                            {store.rating.toFixed(1)}
                          </span>
                        </div>
                      </div>
                    )}
                    {store.isActive ? (
                      <div className="absolute top-2 sm:top-3 left-2 sm:left-3 bg-green-500/95 backdrop-blur-md text-white px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold shadow-sm border border-white/30">
                        Abierto
                      </div>
                    ) : (
                      <div className="absolute top-2 sm:top-3 left-2 sm:left-3 bg-red-500/95 backdrop-blur-md text-white px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold shadow-sm border border-white/30">
                        Cerrado
                      </div>
                    )}
                    {/* Logo del negocio superpuesto - iOS Style */}
                    <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white/95 backdrop-blur-md shadow-sm border border-white/50 overflow-hidden flex items-center justify-center">
                        <StoreImage
                          imageKey={store.logoUrl || null}
                          type="logo"
                          className="w-full h-full object-cover"
                          alt={store.name}
                          storeId={store.id}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-4 sm:p-5 md:p-6 pt-6 sm:pt-7 md:pt-8">
                    <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gray-100 overflow-hidden border border-gray-200">
                        <StoreImage
                          imageKey={store.logoUrl || null}
                          type="logo"
                          className="w-full h-full object-cover"
                          alt={store.name}
                          storeId={store.id}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-base sm:text-lg mb-0.5 sm:mb-1 group-hover:text-primary-600 transition-colors truncate">
                          {store.name}
                        </h3>
                      </div>
                    </div>
                    {store.description && (
                      <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2 leading-relaxed">{store.description}</p>
                    )}

                    <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                      {store.deliveryTime && (
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          <span>{store.deliveryTime} min</span>
                        </div>
                      )}
                      {store.offersDelivery && (
                        <div className="flex items-center space-x-1">
                          <Truck className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          <span>
                            {store.deliveryFee === 0 || store.deliveryFee === null 
                              ? 'Gratis' 
                              : `RD$${Number(store.deliveryFee).toFixed(2)}`}
                          </span>
                        </div>
                      )}
                    </div>

                    {store.categories && store.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 sm:gap-2">
                        {store.categories.slice(0, 3).map((category, index) => (
                          <span
                            key={category.name || index}
                            className="bg-primary-50 text-primary-600 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium"
                          >
                            {category.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  </Link>
                ))}
              </div>
              {/* Indicador de scroll */}
              {featuredStores.length > 3 && (
                <div className="flex justify-center mt-4 gap-2">
                  <div className="text-sm text-gray-500 flex items-center gap-2">
                    <span>Desliza para ver más</span>
                    <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">No hay negocios destacados disponibles en este momento.</p>
            </div>
          )}

          <div className="text-center mt-6 sm:mt-8 md:mt-10">
            <Link 
              to="/stores" 
              className="inline-flex items-center justify-center px-5 sm:px-6 py-3 sm:py-3.5 bg-primary-600 text-white rounded-xl sm:rounded-2xl font-semibold text-sm sm:text-base shadow-sm hover:shadow-md hover:bg-primary-700 transition-all duration-200 active:scale-95 min-h-[44px]"
            >
              Ver Todos los Negocios
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section - Solo para usuarios no logueados - iPhone Optimized */}
      {!isAuthenticated && (
        <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary-600 to-primary-700 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 px-2">
              ¿Eres Propietario de un Negocio?
            </h2>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 md:mb-10 text-primary-100 max-w-2xl mx-auto px-3">
              Únete a ColmaFácil y lleva tu negocio al siguiente nivel.
              Más clientes, mejores ingresos, gestión simplificada.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-3 sm:px-4">
              <Link 
                to="/register" 
                className="inline-flex items-center justify-center px-5 sm:px-6 md:px-8 py-3 sm:py-3.5 md:py-4 bg-white text-primary-600 rounded-xl sm:rounded-2xl font-semibold text-sm sm:text-base md:text-lg shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200 active:scale-95 min-h-[44px]"
              >
                Registrarme como Negocio
              </Link>
              <a 
                href="#features" 
                className="inline-flex items-center justify-center px-5 sm:px-6 md:px-8 py-3 sm:py-3.5 md:py-4 bg-white/10 backdrop-blur-sm border border-white/30 text-white rounded-xl sm:rounded-2xl font-semibold text-sm sm:text-base md:text-lg hover:bg-white/20 transition-all duration-200 active:scale-95 min-h-[44px]"
              >
                Más Información
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
    </>
  )
}

export default Home
